import { CobaltAvatar } from '@cae/cobalt-react/components/avatar';
import { useCobaltLayoutContext } from '@cae/cobalt-react/components/layout';
import { CobaltIconChevronUpSolid } from '@cae/cobalt-react/icons/solid/CobaltIconChevronUpSolid';
import {
  classNames,
  CobaltDynamicTruncateString,
  CobaltPopover,
} from '@cae/cobalt-react/utils';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { useOrganizations } from '@contexts/organizations-provider';
import { getNameParts } from '@shared/helpers/get-name-parts/getNameParts';
import * as React from 'react';

import { OrganizationList } from './components/organization-list/OrganizationList';
import { OrganizationSearch } from './components/organization-search/OrganizationSearch';
import { useOrganizationSwitcherPopover } from './hooks/use-organization-switcher-popover/useOrganizationSwitcherPopover';
import styles from './OrganizationSwitcher.module.css';
import { LISTBOX_ID } from './OrganizationSwitcher.utils';

const OrganizationSwitcher = React.memo((): React.ReactElement => {
  const { organizations, selectedOrganization } = useOrganizations();
  const { isInternalUser } = useAuthenticatedUser();
  const { isSideNavigationOpen } = useCobaltLayoutContext();

  const currentOrg = selectedOrganization!;
  const { firstName, lastName } = getNameParts(currentOrg.name);

  const {
    isOpen,
    popoverAnchor,
    searchQuery,
    activeIndex,
    filteredOrganizations,
    showSearch,
    activeDescendant,
    handleTogglePopover,
    handleClosePopover,
    handleOrganizationSelect,
    handleSearchChange,
    handleKeyDown,
    setActiveIndex,
  } = useOrganizationSwitcherPopover(organizations, currentOrg, isInternalUser);

  const switcherClassName = classNames(
    styles.organizationSwitcher,
    !isSideNavigationOpen && styles.organizationSwitcherCollapsed
  );

  return (
    <>
      <button
        type="button"
        data-testid="organization-switcher"
        className={switcherClassName}
        onClick={handleTogglePopover}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`Switch organization, currently ${currentOrg.name}`}
      >
        <CobaltAvatar firstName={firstName} lastName={lastName} />

        {isSideNavigationOpen && (
          <>
            <div className={styles.organizationInfo}>
              <span className={`${styles.organizationLabel} body-01`}>
                Organization
              </span>
              <span className={`${styles.organizationName} body-02`}>
                <CobaltDynamicTruncateString>
                  {currentOrg.name}
                </CobaltDynamicTruncateString>
              </span>
            </div>

            <CobaltIconChevronUpSolid
              size="small"
              className={classNames(
                styles.chevronIcon,
                isOpen && styles.chevronIconOpen
              )}
              aria-hidden="true"
            />
          </>
        )}
      </button>

      <CobaltPopover
        isOpen={isOpen}
        anchorEl={popoverAnchor}
        position={isSideNavigationOpen ? 'top' : 'top-start'}
        onClose={handleClosePopover}
        className={classNames(
          styles.organizationSwitcherPopover,
          isSideNavigationOpen
            ? ''
            : styles.organizationSwitcherPopoverCollapsed,
          'elevation-04-floating'
        )}
      >
        <div
          role="presentation"
          className={styles.popoverContent}
          onKeyDown={handleKeyDown}
        >
          <OrganizationList
            organizations={filteredOrganizations}
            currentOrgId={currentOrg.id}
            isInternalUser={isInternalUser}
            activeIndex={activeIndex}
            listboxId={LISTBOX_ID}
            onOrganizationSelect={handleOrganizationSelect}
            onClose={handleClosePopover}
            onSetActiveIndex={setActiveIndex}
          />

          {showSearch && (
            <OrganizationSearch
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              listboxId={LISTBOX_ID}
              activeDescendant={activeDescendant}
              isOpen={isOpen}
            />
          )}
        </div>
      </CobaltPopover>
    </>
  );
});

OrganizationSwitcher.displayName = 'OrganizationSwitcher';

export { OrganizationSwitcher };
